// ───────────────────────────────────────────
// Core domain types (shared between client & server)
// Authoritative source; do NOT redefine in client/ or server/.
// ───────────────────────────────────────────

export type Lang = 'zh-CN' | 'zh-HK' | 'en' | 'ja';
export const ALL_LANGS: readonly Lang[] = ['zh-CN', 'zh-HK', 'en', 'ja'] as const;

export type Brand = 'google' | 'aws' | 'huawei';
export const ALL_BRANDS: readonly Brand[] = ['google', 'aws', 'huawei'] as const;

export type LangMap = Record<Lang, string>;
export type BrandMap = Record<Brand, string>;
/**
 * Landing-page URL map: per brand × per lang.
 * Shape: { google: { 'zh-CN': '…', 'zh-HK': '…', en: '…', ja: '…' }, aws: { … } }
 *
 * Why this instead of a flat BrandMap: lets product owners point 日本 viewers
 * to workspace.google.com/intl/ja/ while 英文 viewers see the global URL.
 * Empty lang slot → pickBrandLang falls back to brand.en → google.en → ''.
 */
export type BrandLangMap = Record<Brand, LangMap>;

// ───────────────────────────────────────────
// Product
// ───────────────────────────────────────────

export interface ProductItem {
  id: string;
  name: LangMap;
  description: LangMap;
  audience: LangMap;
  url: BrandLangMap; // per-brand × per-lang; see BrandLangMap above
  isParticipating: boolean;
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
  // Ownership (0002 migration). NULL for legacy rows / deleted users = 孤儿池
  // (super_admin only). ownerEmail is denormalized on read via left-join.
  ownerId: string | null;
  ownerEmail: string | null;
  // Industry tags for the homepage filter (0005 migration). Free-form string[]
  // (UI suggests from `ALL_INDUSTRIES` but doesn't enforce — future industries
  // can be added without code change). Empty array = "all industries"
  // (cross-cutting products like CLM/Expense surface in every industry filter).
  // Optional in type for backward compat with rows created before migration 0005.
  industries?: string[];
  // Soft-delete timestamp (0006 migration). NULL/absent = live product.
  // Non-null = in 回收站 (recycle bin), filtered out of public catalog + AI
  // recommendations, restorable by owner / super_admin. Hard delete removed
  // entirely —误删后可恢复。
  deletedAt?: string | null;
}

// ───────────────────────────────────────────
// Quick scenario card (3-part: role / industry / challenge)
// ───────────────────────────────────────────

export interface QuickOption {
  role: string;
  industry: string;
  challenge: string;
  /**
   * Optional metadata: product IDs this scenario is *intended* to demonstrate.
   * Pure annotation — does NOT affect AI prompts or recommendations (the AI
   * still picks freely from the active product catalog). Used by the admin
   * panel's coverage warning to flag products that no scenario surfaces.
   * Empty / missing array = unlabelled (counts as "no coverage" for warning).
   */
  productIds?: string[];
}

// ───────────────────────────────────────────
// LLM provider chain (admin-editable, stored in global_config.llm_chain)
// Order = fallback priority. `enabled: false` entries are skipped at runtime.
// Keys (API keys) are NOT stored here — they live in server env vars per provider.
// ───────────────────────────────────────────

export type LlmProviderId = 'gemini' | 'kimi' | 'deepseek';
export const ALL_LLM_PROVIDERS: readonly LlmProviderId[] = ['gemini', 'kimi', 'deepseek'] as const;

export interface LlmChainItem {
  providerId: LlmProviderId;
  model: string;
  enabled: boolean;
}

export type LlmChain = LlmChainItem[];

// Model suggestions surfaced in the admin UI combobox. Users may also type any custom
// model ID — this list is just autocomplete hints, not an allowlist.
//
// Curated for text-chat use (no TTS/vision-image/embedding/robotics variants). Newest at
// top. Source of truth per provider (re-check if models drift):
//   gemini:   https://ai.google.dev/gemini-api/docs/models
//   kimi:     https://platform.kimi.ai/docs/models
//   deepseek: https://api-docs.deepseek.com/quick_start/pricing
export const LLM_MODEL_PRESETS: Record<LlmProviderId, readonly string[]> = {
  gemini: [
    'gemini-3.1-pro-preview',
    'gemini-3-flash-preview',
    'gemini-3.1-flash-lite-preview',
    'gemini-2.5-pro',
    'gemini-2.5-flash',
    'gemini-2.5-flash-lite',
  ],
  kimi: [
    'kimi-k2.6',
    'kimi-k2.5',
    'kimi-k2-0905-preview',
    'kimi-k2-thinking',
    'kimi-k2-thinking-turbo',
    'kimi-k2-turbo-preview',
    'moonshot-v1-128k',
    'moonshot-v1-32k',
    'moonshot-v1-8k',
    'kimi-latest',
  ],
  deepseek: ['deepseek-chat', 'deepseek-reasoner'],
};

// ───────────────────────────────────────────
// Global config (single row in DB)
// ───────────────────────────────────────────

export interface GlobalConfig {
  brand: Brand;
  llmChain: LlmChain;
  temperature: number;
  updatedAt: string;
  updatedBy: string | null;
}

// ───────────────────────────────────────────
// AI recommendation output
// ───────────────────────────────────────────

export interface Solution {
  selectedProducts: string[]; // length === 3, ids must be in activeProducts
  rationale: Record<string, string>; // key = productId, value = reason in user's language
}

// ───────────────────────────────────────────
// API request/response shapes
// ───────────────────────────────────────────

export interface GenerateRequest {
  userInput: string;
  lang: Lang;
}

export interface GenerateResponse extends Solution {
  /**
   * Which (providerId, model) actually produced this answer, after walking
   * the chain. Optional for backward-compat (callers who don't care can
   * ignore). Surfaced to UI so users can see "this recommendation was
   * generated by gemini-2.5-pro" — antidote to "black box" anxiety when
   * the chain falls through to a different model than the lecturer expects.
   */
  provider?: LlmProviderId;
  model?: string;
  /** End-to-end server latency in ms (DB read + LLM call + parse). */
  latencyMs?: number;
}

export interface SttRequest {
  // multipart/form-data: { audio: Blob, lang: Lang }
  lang: Lang;
}

export interface SttResponse {
  text: string;
}

// Admin user role. editor = 只管自己名下的产品；super_admin = 全产品 + LLM 链 + Brand 切换
export type UserRole = 'editor' | 'super_admin';
export const ALL_USER_ROLES: readonly UserRole[] = ['editor', 'super_admin'] as const;

// Whitelist row (matches admin_users table). userId is null until first OAuth login.
export interface AdminUser {
  email: string;
  userId: string | null;
  role: UserRole;
  invitedAt: string;
  activatedAt: string | null;
  invitedBy: string | null;
}

// Current logged-in identity returned by GET /admin/me
export interface AuthedUser {
  id: string;
  email: string;
  role: UserRole;
}

export interface InviteUserRequest {
  email: string;
  role: UserRole;
}

export interface GeoResponse {
  country: string | null; // ISO 3166-1 alpha-2, e.g. 'CN', 'HK', 'JP'; null if lookup fails
  lang: Lang; // resolved language from country (or fallback 'en')
}

// ───────────────────────────────────────────
// Structured API errors (always include `code`)
// ───────────────────────────────────────────

export type ApiErrorCode =
  | 'LLM_REQUIRED'
  | 'LLM_CALL_FAILED'
  | 'AI_PARSE'
  | 'AI_INVALID'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_WHITELISTED' // valid Google login but email not in admin_users
  | 'OWNERSHIP_REQUIRED' // editor tried to modify a product they don't own
  | 'VALIDATION'
  | 'NOT_FOUND'
  | 'CONFLICT' // e.g. clone id collision after exhausting suffixes
  | 'RATE_LIMITED'
  | 'INTERNAL';

export interface ApiError {
  code: ApiErrorCode;
  message: string;
  details?: unknown;
}

// ───────────────────────────────────────────
// Brand theme (frontend-only const, exported for type-safety)
// ───────────────────────────────────────────

export interface BrandTheme {
  id: Brand;
  logoUrl: string;
  headerTitle: LangMap;
  accent: string;
  accentMuted: string;
}
